import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CEOSanctuary — the calm-first view of the Cockpit.
 *
 * Shannon's directive: "A busy dashboard does nothing for me but give
 * me anxiety. My business needs to be pushed completely streamlined
 * on spiritual peace, no friction, and fair. Job creation and
 * building community."
 *
 * This view is what she sees first every time she opens the Cockpit.
 * It surfaces at most FOUR pieces of information:
 *
 *   1. A time-aware greeting (no data, just recognition)
 *   2. The single most urgent trigger (or "All clear · rest today")
 *   3. Today's action posture (turns booked · community-pulse read)
 *   4. Exactly one primary CTA
 *
 * A soft "Show operational detail" toggle at the bottom unlocks the
 * full LandlordTriggers + Ledger + action-row experience for when she
 * is in execution mode. Default: sanctuary.
 */

const TIME_BLESSING = () => {
  const h = new Date().getHours();
  if (h < 5)  return { greeting: "Rest deeply, Shannon.",   sub: "The system is holding the night watch." };
  if (h < 12) return { greeting: "Good morning, Shannon.",   sub: "Fresh air. Clear ledger. Fair day ahead." };
  if (h < 17) return { greeting: "Good afternoon, Shannon.", sub: "You're in the middle of building community." };
  if (h < 21) return { greeting: "Good evening, Shannon.",   sub: "Wind down. The digital platform is closing the loop for you." };
  return       { greeting: "Peace tonight, Shannon.",        sub: "Everything critical is caught. Nothing needs you until morning." };
};

const CEOSanctuary = ({ onEnterOperational }) => {
  const [topTrigger, setTopTrigger] = useState(null);
  const [triggerLoading, setTriggerLoading] = useState(true);
  const { greeting, sub } = TIME_BLESSING();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let addr = "";
      try { addr = sessionStorage.getItem("mf_last_address") || ""; } catch { /* ignore */ }
      if (!addr) { setTriggerLoading(false); return; }
      try {
        const q = encodeURIComponent(addr);
        const r = await fetch(`${BACKEND_URL}/api/property/triggers?address=${q}`, { credentials: "include" });
        if (r.ok) {
          const d = await r.json();
          const trigs = d.triggers || [];
          const critical = trigs.find(t => t.severity === "critical")
                        || trigs.find(t => t.severity === "warning")
                        || trigs[0]
                        || null;
          if (!cancelled) setTopTrigger(critical);
        }
      } catch { /* silent */ }
      if (!cancelled) setTriggerLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const severityColor = (t) => {
    if (!t) return "#00DC78";
    if (t.severity === "critical") return "#F87171";
    if (t.severity === "warning")  return "#FFB347";
    return "#5AC8D8";
  };

  return (
    <div data-testid="ceo-sanctuary" style={sanctuary}>
      {/* Calm greeting */}
      <div style={{ textAlign: "center" }}>
        <div style={eyebrow}>MOOVIN FORWARD LLC · CEO GATHERING SPACE</div>
        <h1 style={h1}>{greeting}</h1>
        <p style={subLead}>{sub}</p>
      </div>

      {/* Single focus tile */}
      <div style={focusTile}>
        {triggerLoading ? (
          <div style={muted}>Listening to the compliance stream…</div>
        ) : topTrigger ? (
          <>
            <div style={{ ...focusHeader, color: severityColor(topTrigger) }}>
              One thing needs your attention
            </div>
            <div style={focusTitle}>{topTrigger.title}</div>
            <div style={focusDetail}>{topTrigger.detail}</div>
            {topTrigger.action_href && (
              <a href={topTrigger.action_href}
                 data-testid="ceo-sanctuary-primary-cta"
                 style={{ ...primaryCta, background: severityColor(topTrigger) }}>
                {topTrigger.action_label}
              </a>
            )}
          </>
        ) : (
          <>
            <div style={{ ...focusHeader, color: "#00DC78" }}>
              All clear · Nothing urgent
            </div>
            <div style={focusTitle}>Today is a building day, not a fixing day.</div>
            <div style={focusDetail}>
              The digital platform is holding every clock, ledger, and trigger for you. Rest, build community, create jobs, and check back when you feel called.
            </div>
            <a href="/expo" data-testid="ceo-sanctuary-primary-cta" style={{ ...primaryCta, background: "#00DC78" }}>
              Open the demo surface
            </a>
          </>
        )}
      </div>

      {/* Three-word ethos strip */}
      <div style={ethos}>
        <div style={ethosPill}>Spiritual peace</div>
        <div style={ethosPill}>No friction</div>
        <div style={ethosPill}>Fair pay</div>
        <div style={ethosPill}>Job creation</div>
        <div style={ethosPill}>Community</div>
      </div>

      {/* Optional escape hatch */}
      <button data-testid="ceo-sanctuary-enter-operational"
              onClick={onEnterOperational}
              style={enterOpBtn}>
        Show operational detail
      </button>
    </div>
  );
};

// ─── styles ─────────────────────────────────────────────────────────
const sanctuary = {
  background: "linear-gradient(180deg, rgba(90,200,216,0.05) 0%, rgba(3,11,26,0) 60%)",
  border: "1px solid rgba(90,200,216,0.16)",
  borderRadius: 20,
  padding: "36px 28px 26px",
  display: "flex",
  flexDirection: "column",
  gap: 22,
  alignItems: "stretch",
};
const eyebrow = {
  color: "#5AC8D8", fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.66rem", letterSpacing: "0.32em", fontWeight: 800,
};
const h1 = {
  fontSize: "1.85rem", fontWeight: 900, letterSpacing: "-0.01em",
  margin: "10px 0 6px",
  color: "#fff",
};
const subLead = {
  color: "#B4BEC8", fontSize: "0.98rem", margin: 0, lineHeight: 1.55,
  maxWidth: 480, marginLeft: "auto", marginRight: "auto",
  fontStyle: "italic",
};
const focusTile = {
  background: "rgba(255,255,255,0.02)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 14,
  padding: "24px 22px 22px",
  textAlign: "center",
  maxWidth: 620,
  marginLeft: "auto", marginRight: "auto",
  width: "100%",
  boxSizing: "border-box",
};
const focusHeader = {
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.68rem", letterSpacing: "0.24em", fontWeight: 800,
  marginBottom: 10,
};
const focusTitle = {
  color: "#fff", fontSize: "1.15rem", fontWeight: 800,
  lineHeight: 1.4, marginBottom: 8,
};
const focusDetail = {
  color: "#B4BEC8", fontSize: "0.88rem", lineHeight: 1.6,
  marginBottom: 18,
};
const primaryCta = {
  display: "inline-block",
  padding: "10px 22px",
  borderRadius: 999,
  color: "#03151F",
  fontWeight: 800,
  fontSize: "0.9rem",
  letterSpacing: "0.02em",
  textDecoration: "none",
};
const muted = { color: "#7E92A8", fontSize: "0.9rem" };
const ethos = {
  display: "flex", flexWrap: "wrap", justifyContent: "center",
  gap: 8,
};
const ethosPill = {
  padding: "5px 13px",
  border: "1px solid rgba(232,184,196,0.28)",
  color: "#E8B8C4",
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.66rem", letterSpacing: "0.14em", fontWeight: 700,
  borderRadius: 999,
};
const enterOpBtn = {
  alignSelf: "center",
  background: "transparent",
  border: "1px dashed rgba(232,184,196,0.4)",
  color: "#E8B8C4",
  fontSize: "0.72rem", letterSpacing: "0.14em", fontWeight: 700,
  padding: "6px 14px", borderRadius: 999,
  cursor: "pointer",
  fontFamily: "'JetBrains Mono', monospace",
};

export default CEOSanctuary;
